/**
 * F1. Экзамен: при решениях `school.upperSec` и `school.tertiary` пять
 * коротких вопросов (столица, арифметика, «что дороже»), по 4 варианта.
 * Знания гасят неверные варианты: при 80 — три из трёх, при 30 — ни одного
 * (`examHiddenWrong`, линейно между). ≥ 3 верных — поступил: вариант
 * `school` (старшая школа) или `university`. Если он недоступен в решении
 * (нет денег, низкие знания), экзамен не предлагается (`available` = false):
 * поступать некуда. Провал и «пропустить» — автовыбор (`timeoutOption`,
 * вероятность из плана 1).
 *
 * Банк: 60 вопросов, по 20 на тему; тексты — ключи `minigame.exam.<тема>.<n>`
 * и `.<n>.<i>` в @life/i18n, верный индекс — здесь.
 */
import type { Dataset, GamblingLaw } from '@life/data';
import type { Decision, Life, MinigameOutcome, Rng } from '@life/engine';
import type { MinigameDef } from '../types';
import { nothing, optionAvailable, pendingOf, shuffle } from './common';

export const EXAM_QUESTIONS = 5;
export const EXAM_OPTIONS = 4;
export const EXAM_PASS_CORRECT = 3;
/** Знания, при которых ещё ни один неверный вариант не погашен / все три погашены. */
export const EXAM_KNOWLEDGE_NONE = 30;
export const EXAM_KNOWLEDGE_ALL = 80;

export const EXAM_TOPICS = ['geo', 'math', 'price'] as const;
export type ExamTopic = (typeof EXAM_TOPICS)[number];

export interface ExamQuestionDef {
  topic: ExamTopic;
  n: number;
  /** Индекс верного варианта 0..3. */
  correct: number;
}

const CORRECT: Readonly<Record<ExamTopic, readonly number[]>> = {
  geo: [1, 0, 2, 3, 1, 1, 2, 0, 1, 2, 1, 2, 3, 0, 1, 2, 0, 2, 3, 2],
  math: [1, 0, 2, 3, 1, 2, 0, 2, 2, 2, 0, 1, 1, 2, 2, 0, 0, 0, 2, 2],
  price: [1, 0, 2, 3, 0, 2, 1, 0, 1, 0, 1, 0, 0, 1, 1, 0, 0, 0, 0, 2],
};

export const EXAM_BANK: readonly ExamQuestionDef[] = EXAM_TOPICS.flatMap((topic) =>
  CORRECT[topic].map((correct, n) => ({ topic, n, correct })),
);

export function examQuestionKey(q: ExamQuestionDef): string {
  return `minigame.exam.${q.topic}.${q.n}`;
}

export function examOptionKey(q: ExamQuestionDef, i: number): string {
  return `${examQuestionKey(q)}.${i}`;
}

/** Сколько из трёх неверных вариантов погашено: 0 при знаниях ≤ 30, 3 при ≥ 80, линейно между. */
export function examHiddenWrong(knowledge: number): number {
  const k = Number.isFinite(knowledge) ? knowledge : 0;
  const share = (k - EXAM_KNOWLEDGE_NONE) / (EXAM_KNOWLEDGE_ALL - EXAM_KNOWLEDGE_NONE);
  return Math.max(0, Math.min(EXAM_OPTIONS - 1, Math.round(share * (EXAM_OPTIONS - 1))));
}

export interface ExamQuestion {
  key: string;
  topic: ExamTopic;
  /** Ключи четырёх вариантов. */
  options: string[];
  correct: number;
  /** Индексы неверных вариантов, погашенных знаниями. */
  hidden: number[];
}

export interface ExamState {
  questions: ExamQuestion[];
  hiddenWrong: number;
  /** Вариант решения при ≥ 3 верных. */
  passOption: string;
  /** Вариант при провале и «пропустить» — автовыбор решения. */
  failOption: string;
}

export type ExamInput = { answers: number[] };

const EXAM_KINDS = ['school.upperSec', 'school.tertiary'] as const;
const PASS_OPTION: Readonly<Record<(typeof EXAM_KINDS)[number], string>> = { 'school.upperSec': 'school', 'school.tertiary': 'university' };

function passOptionOf(decision: Decision): string {
  return PASS_OPTION[decision.kind as (typeof EXAM_KINDS)[number]] ?? '';
}

function drawQuestions(rng: Rng): ExamQuestionDef[] {
  // По одному вопросу с каждой темы, остальные — из оставшихся.
  const byTopic = EXAM_TOPICS.map((topic) => shuffle(EXAM_BANK.filter((q) => q.topic === topic), rng));
  const picked = byTopic.map((qs) => qs[0] as ExamQuestionDef);
  const rest = shuffle(byTopic.flatMap((qs) => qs.slice(1)), rng);
  return shuffle([...picked, ...rest.slice(0, EXAM_QUESTIONS - picked.length)], rng);
}

export const exam: MinigameDef<ExamInput, ExamState> = {
  id: 'exam',
  section: 'F',
  triggers: [
    { kind: 'decision', decision: 'school.upperSec' },
    { kind: 'decision', decision: 'school.tertiary' },
  ],
  available(life: Life, _ds: Dataset, _law: GamblingLaw): boolean {
    const decision = pendingOf(life, EXAM_KINDS);
    return decision !== null && optionAvailable(decision, passOptionOf(decision));
  },
  setup(life: Life, _ds: Dataset, rng: Rng): ExamState {
    const decision = pendingOf(life, EXAM_KINDS);
    const failOption = decision?.timeoutOption ?? '';
    const wanted = decision ? passOptionOf(decision) : '';
    const passOption = decision && optionAvailable(decision, wanted) ? wanted : failOption;
    const hiddenWrong = examHiddenWrong(life.stats.knowledge);
    const questions = drawQuestions(rng).map((q) => {
      const wrong = [0, 1, 2, 3].filter((i) => i !== q.correct);
      const hidden = shuffle(wrong, rng).slice(0, hiddenWrong).sort((a, b) => a - b);
      return {
        key: examQuestionKey(q),
        topic: q.topic,
        options: [0, 1, 2, 3].map((i) => examOptionKey(q, i)),
        correct: q.correct,
        hidden,
      };
    });
    return { questions, hiddenWrong, passOption, failOption };
  },
  play(state: ExamState, input: ExamInput, _life: Life): MinigameOutcome {
    const answers = Array.isArray(input.answers) ? input.answers : [];
    let correct = 0;
    state.questions.forEach((q, i) => {
      if (answers[i] === q.correct) correct += 1;
    });
    const passed = correct >= EXAM_PASS_CORRECT;
    return {
      ...nothing(),
      decisionOption: passed ? state.passOption : state.failOption,
      logKey: passed ? 'minigame.exam.pass' : 'minigame.exam.fail',
      logParams: { correct, total: state.questions.length },
    };
  },
  expected(state: ExamState): MinigameOutcome {
    return { ...nothing(), decisionOption: state.failOption };
  },
};
